import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2, ShieldCheck, Copy, AlertCircle, Trash2, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminTotpStatus, adminTotpSetup, adminTotpEnable, adminTotpDisable,
  adminTotpRegenBackupCodes,
} from "../../lib/api";

const QR = ({ uri }) => {
  // Render via the qr-server.com public API (no backend dependency)
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(uri)}&bgcolor=131313&color=e4dfd3&margin=8`;
  return (
    <img
      src={src}
      alt="TOTP QR code"
      data-testid="totp-qr"
      className="w-60 h-60 border border-[#2D2A26]"
    />
  );
};

export const TotpSecurityPanel = ({ opToken, userToken }) => {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await adminTotpStatus(userToken)); }
    catch { setStatus({ totp_enabled: false }); }
  }, [userToken]);
  useEffect(() => { load(); }, [load]);

  const beginSetup = async () => {
    setBusy(true);
    try {
      const r = await adminTotpSetup(opToken, userToken);
      setSetupData(r); setVerifyCode("");
    } catch { toast.error("Could not start TOTP setup"); }
    finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    const c = verifyCode.replace(/\D/g, "");
    if (c.length !== 6) { toast.error("Enter the 6-digit code from your app"); return; }
    setBusy(true);
    try {
      const r = await adminTotpEnable(opToken, userToken, c);
      setBackupCodes(r.backup_codes);
      setSetupData(null);
      load();
      toast.success("Authenticator app enabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify code");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable authenticator app for admin? You'll need to re-enrol on next sign-in.")) return;
    try {
      await adminTotpDisable(opToken, userToken);
      toast.success("TOTP disabled");
      load(); setSetupData(null); setBackupCodes(null);
    } catch { toast.error("Disable failed"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Generate 8 new backup codes? All existing backup codes will stop working.")) return;
    setBusy(true);
    try {
      const r = await adminTotpRegenBackupCodes(opToken, userToken);
      setBackupCodes(r.backup_codes);
      toast.success("New backup codes generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally { setBusy(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* noop */ }
  };

  const enabled = !!status?.totp_enabled;

  return (
    <div data-testid="admin-security-panel">
      <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// AUTHENTICATOR APP (2FA)</div>
      <h1 className="font-display text-4xl text-[#E4DFD3] mb-3">Security</h1>
      <p className="text-[#A39E93] max-w-2xl mb-8 leading-relaxed">
        Use a TOTP authenticator app (Google Authenticator, Authy, 1Password) as your
        second factor. Codes are generated on your device — no email delivery
        involved. Backup codes are issued at enrolment and can be rotated below.
      </p>

      <div className="border border-[#2D2A26] bg-[#131313] p-6 mb-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <ShieldCheck size={16} className={enabled ? "text-[#5DD4B3]" : "text-[#757065]"} />
            <span className="font-display text-xl text-[#E4DFD3]">
              {enabled ? "Authenticator app enabled" : "Authenticator app not set up"}
            </span>
          </div>
          {enabled ? (
            <div className="flex items-center gap-2">
              <button onClick={regenerate} disabled={busy} data-testid="totp-regen-btn"
                className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]">
                <KeyRound size={11} /> Rotate backup codes
              </button>
              <button onClick={disable} className="inline-flex items-center gap-1.5 border border-[#D92D20]/40 hover:bg-[#D92D20]/15 text-[#D92D20] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]">
                <Trash2 size={11} /> Disable
              </button>
            </div>
          ) : (
            <button onClick={beginSetup} disabled={busy} data-testid="totp-setup-btn"
              className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />}
              Set up
            </button>
          )}
        </div>
      </div>

      {setupData && (
        <div className="border border-[#D92D20]/60 bg-[#131313] p-6 mb-6" data-testid="totp-setup-panel">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">// SCAN OR PASTE INTO APP</div>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <QR uri={setupData.otpauth_uri} />
            </div>
            <div>
              <div className="mb-4">
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">// MANUAL KEY</span>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 font-mono text-sm tracking-[0.15em] bg-[#0a0a0a] border border-[#2D2A26] px-3 py-2 text-[#E4DFD3] break-all" data-testid="totp-secret">
                    {setupData.secret}
                  </code>
                  <button onClick={() => copy(setupData.secret)} className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2 py-2 font-mono text-[9px]">
                    <Copy size={11} />
                  </button>
                </div>
              </div>
              <div className="mb-4">
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">// ENTER CURRENT CODE</span>
                <input type="text" inputMode="numeric" maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0,6))}
                  onKeyDown={(e) => { if (e.key === "Enter") confirmEnable(); }}
                  placeholder="000000"
                  data-testid="totp-confirm-input"
                  className="mt-1 w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-2xl tracking-[0.4em] text-center text-[#E4DFD3] placeholder:text-[#454038]" />
              </div>
              <div className="flex gap-2">
                <button onClick={confirmEnable} disabled={busy || verifyCode.length !== 6}
                  data-testid="totp-confirm-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                  Verify & enable
                </button>
                <button onClick={() => setSetupData(null)} className="border border-[#454038] text-[#A39E93] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {backupCodes && (
        <div className="border border-[#5DD4B3]/50 bg-[#0a1612] p-6 mb-6" data-testid="totp-backup-codes">
          <div className="flex items-center gap-2 mb-3 text-[#5DD4B3]">
            <AlertCircle size={14} />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// BACKUP CODES — SHOWN ONCE</span>
          </div>
          <p className="text-sm text-[#A39E93] mb-4">
            Store these 8 single-use backup codes offline (password manager, printed).
            Use one if you lose your authenticator. They will not be shown again.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {backupCodes.map((c) => (
              <code key={c} className="font-mono text-sm tracking-[0.1em] bg-[#0a0a0a] border border-[#2D2A26] px-3 py-2 text-[#E4DFD3] text-center">{c}</code>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => copy(backupCodes.join("\n"))} className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]">
              <Copy size={11} /> Copy all
            </button>
            <button onClick={() => setBackupCodes(null)} className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#E4DFD3] px-3 py-2">
              I've saved them
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
